import React, { useRef } from 'react';

// ─── Clinical 2D photo series ────────────────────────────────────────────────
// A guided capture template (like the standard ortho/implant photo set): each
// slot has a line-art guide + label, tap to upload one photo. The slot's label
// becomes the photo type, so uploads flow straight into the preop2d pipeline.

const GOLD = '#c8922a';

// Guide illustrations (inline SVG, brand-free). Clean, consistent 2px line icons.
const svgProps = {
  viewBox: '0 0 48 48', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  width: '100%', height: '100%',
};
const dot = { fill: 'currentColor', stroke: 'none' };

const Guides = {
  // Side profile — head facing left, soft jaw + brow + nose notch
  profile: (
    <svg {...svgProps}>
      <path d="M33 12c-3.6-2.2-8.4-2.2-11.6.6-2.4 2.1-3 5-3.4 7.6-.2 1.4-1.2 2-2.3 2.7-1 .7-2 1.6-1.6 2.8.3.9 1.3 1 2 1.4.6.4.4 1.3.3 2-.3 2.1 0 4.3 1.9 5.6 1.9 1.3 4.4 1.4 6.7 1.3" />
      <circle cx="27.5" cy="20.5" r="1.1" style={dot} />
    </svg>
  ),
  // Front, relaxed lips
  frontalRest: (
    <svg {...svgProps}>
      <path d="M24 9c6.6 0 11 4.8 11 12.5S30.6 38 24 38s-11-8.8-11-16.5S17.4 9 24 9Z" />
      <circle cx="19.5" cy="21" r="1.2" style={dot} />
      <circle cx="28.5" cy="21" r="1.2" style={dot} />
      <path d="M24 24v3.5" />
      <path d="M20.5 31.5h7" />
    </svg>
  ),
  // Front, smiling (teeth showing)
  frontalSmile: (
    <svg {...svgProps}>
      <path d="M24 9c6.6 0 11 4.8 11 12.5S30.6 38 24 38s-11-8.8-11-16.5S17.4 9 24 9Z" />
      <circle cx="19.5" cy="21" r="1.2" style={dot} />
      <circle cx="28.5" cy="21" r="1.2" style={dot} />
      <path d="M24 24v3" />
      <path d="M19 29.5c2.6 3.4 7.4 3.4 10 0" />
    </svg>
  ),
  // Maxillary arch, occlusal view — horseshoe opening toward throat (down)
  upperArch: (
    <svg {...svgProps}>
      <path d="M14 14c0 13 4 21 10 21s10-8 10-21" />
      <path d="M14 14c2.6-2.6 17.4-2.6 20 0" />
      <path d="M18.5 17v3.2M24 16.2v3.2M29.5 17v3.2M15.5 24v3.2M32.5 24v3.2" />
    </svg>
  ),
  // Mandibular arch, occlusal view — horseshoe opening up
  lowerArch: (
    <svg {...svgProps}>
      <path d="M14 34c0-13 4-21 10-21s10 8 10 21" />
      <path d="M14 34c2.6 2.6 17.4 2.6 20 0" />
      <path d="M18.5 31v-3.2M24 31.8v-3.2M29.5 31v-3.2M15.5 24v-3.2M32.5 24v-3.2" />
    </svg>
  ),
  // Right lateral bite — two tooth rows in occlusion, side marker right
  rightBite: (
    <svg {...svgProps}>
      <rect x="11" y="18.5" width="26" height="5.2" rx="1.6" />
      <rect x="11" y="24.3" width="26" height="5.2" rx="1.6" />
      <path d="M17 18.5v5.2M23 18.5v5.2M29 18.5v5.2M35 18.5v5.2" />
      <path d="M17 24.3v5.2M23 24.3v5.2M29 24.3v5.2" />
      <path d="M37 14.5l3 2.5-3 2.5" />
    </svg>
  ),
  // Retracted frontal — upper + lower anterior teeth, framed
  anteriorBite: (
    <svg {...svgProps}>
      <rect x="15" y="16" width="18" height="16" rx="2.5" />
      <path d="M15 24h18" />
      <path d="M21 16v16M27 16v16" />
    </svg>
  ),
  // Left lateral bite — mirror of right, side marker left
  leftBite: (
    <svg {...svgProps}>
      <rect x="11" y="18.5" width="26" height="5.2" rx="1.6" />
      <rect x="11" y="24.3" width="26" height="5.2" rx="1.6" />
      <path d="M13 18.5v5.2M19 18.5v5.2M25 18.5v5.2M31 18.5v5.2" />
      <path d="M19 24.3v5.2M25 24.3v5.2M31 24.3v5.2" />
      <path d="M11 14.5l-3 2.5 3 2.5" />
    </svg>
  ),
};

// The standard series, grouped. `id` is stable; `label` is the photo type stored
// on upload (kept consistent with PREOP_PHOTO_TYPES in NewCase).
export const PHOTO_TEMPLATE_GROUPS = [
  {
    group: 'Facial',
    slots: [
      { id: 'profile',      label: 'Profile Smile',   guide: 'profile' },
      { id: 'frontalRest',  label: 'Relaxed Lip',     guide: 'frontalRest' },
      { id: 'frontalSmile', label: 'Frontal Smile',   guide: 'frontalSmile' },
    ],
  },
  {
    group: 'Occlusal',
    slots: [
      { id: 'upperOcclusal', label: 'Upper Occlusal', guide: 'upperArch' },
      { id: 'lowerOcclusal', label: 'Lower Occlusal', guide: 'lowerArch' },
    ],
  },
  {
    group: 'Intraoral / Bite',
    slots: [
      { id: 'rightLateral', label: 'Right Lateral Smile', guide: 'rightBite' },
      { id: 'anteriorBite', label: 'Retracted Frontal',   guide: 'anteriorBite' },
      { id: 'leftLateral',  label: 'Left Lateral Smile',  guide: 'leftBite' },
    ],
  },
];

export const PHOTO_TEMPLATE_SLOTS = PHOTO_TEMPLATE_GROUPS.flatMap(g => g.slots);

function Slot({ slot, file, onPick, onRemove }) {
  const inputRef = useRef();
  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      <div
        onClick={() => !file && inputRef.current?.click()}
        style={{
          position: 'relative',
          width: '100%', aspectRatio: '1 / 1',
          borderRadius: '12px',
          border: file ? `2px solid #22c55e` : '2px dashed #d1d5db',
          background: file ? '#f0fdf4' : '#fafafa',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: file ? 'default' : 'pointer',
          color: '#9aa0a6', overflow: 'hidden', transition: 'all 0.2s',
        }}
      >
        {file ? (
          <>
            <img src={preview} alt={slot.label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              style={{
                position: 'absolute', top: '4px', right: '4px',
                width: '20px', height: '20px', borderRadius: '50%',
                border: 'none', background: 'rgba(0,0,0,0.55)', color: 'white',
                cursor: 'pointer', fontSize: '13px', lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >×</button>
          </>
        ) : (
          <>
            <div style={{ width: '52%', height: '52%' }}>{Guides[slot.guide]}</div>
            <div style={{
              position: 'absolute', top: '6px', right: '6px',
              width: '20px', height: '20px', borderRadius: '50%',
              background: GOLD, color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '15px', lineHeight: 1, fontWeight: 700,
            }}>+</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.jpg,.jpeg,.png,.heic"
          style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); e.target.value = ''; }}
        />
      </div>
      <div style={{ fontSize: '11px', color: file ? '#16a34a' : '#666', textAlign: 'center', fontWeight: file ? 600 : 400 }}>
        {slot.label}
      </div>
    </div>
  );
}

export default function PhotoTemplate({ photos, setPhotos }) {
  const count = Object.keys(photos).length;
  const total = PHOTO_TEMPLATE_SLOTS.length;

  const pick = (id, file) => setPhotos(prev => ({ ...prev, [id]: file }));
  const remove = (id) => setPhotos(prev => { const n = { ...prev }; delete n[id]; return n; });

  return (
    <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: '16px 18px', background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#111' }}>Clinical Photo Series</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
            Tap a slot to add that photo — the type is set automatically
          </div>
        </div>
        <span style={{
          fontSize: '12px', fontWeight: 600,
          color: count === total ? '#16a34a' : GOLD,
        }}>{count} / {total}</span>
      </div>

      {PHOTO_TEMPLATE_GROUPS.map(g => (
        <div key={g.group} style={{ marginBottom: '14px' }}>
          <div style={{
            fontSize: '11px', fontWeight: 600, color: '#999',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px',
          }}>{g.group}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {g.slots.map(slot => (
              <Slot
                key={slot.id}
                slot={slot}
                file={photos[slot.id]}
                onPick={(f) => pick(slot.id, f)}
                onRemove={() => remove(slot.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
